import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import { AppHeader } from '../../shared/components/AppComponents';
import './FlightFinder.css';

const timePreferenceLabels = {
  0: 'Morning (5–12)',
  1: 'Afternoon (12–18)',
  2: 'Evening (18–5)',
  3: 'Flexible'
};

const statusLabels = {
  0: '⏳ Pending',
  1: '🔍 Searching...',
  2: '✅ Completed',
  3: '❌ Failed'
};

const statusKeys = { 0: 'pending', 1: 'running', 2: 'completed', 3: 'failed' };

const unwrap = d => Array.isArray(d) ? d : (d?.$values || []).filter(x => x && !x.$ref);

export default function FlightFinder() {
  const { instance } = useMsal();
  const [searches, setSearches] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'calendar'
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newSearch, setNewSearch] = useState({
    origin: 'OSL',
    destination: 'LHR',
    dateRangeStart: '',
    dateRangeEnd: '',
    tripDurationMinDays: 3,
    tripDurationMaxDays: 4,
    passengers: 1,
    weekdayTimePreference: 1,  // Afternoon
    weekendTimePreference: 3   // Flexible
  });

  const fetchSearches = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/flightsearch');
      setSearches(unwrap(data));
    } catch (err) {
      console.error('Failed to load searches:', err);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchSearches(); }, [fetchSearches]);

  // Poll for running searches
  useEffect(() => {
    const running = searches.some(s => s.status === 0 || s.status === 1);
    if (!running) return;
    const interval = setInterval(fetchSearches, 5000);
    return () => clearInterval(interval);
  }, [searches, fetchSearches]);

  const handleCreateSearch = async (e) => {
    e.preventDefault();
    try {
      const result = await apiFetchJson(instance, '/api/flightsearch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSearch)
      });
      setShowForm(false);
      setNewSearch(ns => ({ ...ns, dateRangeStart: '', dateRangeEnd: '' }));
      await fetchSearches();
      if (result?.id) await openSearch(result.id);
    } catch (err) {
      console.error('Failed to create search:', err);
    }
  };

  const openSearch = async (id) => {
    try {
      const data = await apiFetchJson(instance, `/api/flightsearch/${id}`);
      setSelectedSearch(data);

      try {
        const cal = await apiFetchJson(instance, `/api/flightsearch/${id}/calendar`);
        setCalendarData(unwrap(cal));
      } catch { setCalendarData([]); }
    } catch (err) {
      console.error('Failed to load search:', err);
    }
  };

  const toggleFavorite = async (resultId) => {
    try {
      await apiFetchJson(instance, `/api/flightsearch/results/${resultId}/favorite`, { method: 'PUT' });
      if (selectedSearch) await openSearch(selectedSearch.id);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const deleteSearch = async (id) => {
    try {
      await apiFetchJson(instance, `/api/flightsearch/${id}`, { method: 'DELETE' });
      if (selectedSearch?.id === id) setSelectedSearch(null);
      await fetchSearches();
    } catch (err) {
      console.error('Failed to delete search:', err);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const formatDay = (d) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';

  // --- Render: Search list ---
  const renderSearchList = () => (
    <div className="flight-section">
      <div className="flight-section-header">
        <h3>🔍 My Flight Searches</h3>
        <button className="flight-btn primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Search'}
        </button>
      </div>

      {showForm && (
        <form className="flight-form" onSubmit={handleCreateSearch}>
          <div className="flight-form-row">
            <div className="flight-field">
              <label>From (IATA)</label>
              <input value={newSearch.origin} onChange={e => setNewSearch({ ...newSearch, origin: e.target.value.toUpperCase() })} required maxLength={3} placeholder="OSL" />
            </div>
            <div className="flight-field">
              <label>To (IATA)</label>
              <input value={newSearch.destination} onChange={e => setNewSearch({ ...newSearch, destination: e.target.value.toUpperCase() })} required maxLength={3} placeholder="LHR" />
            </div>
            <div className="flight-field">
              <label>Passengers</label>
              <input type="number" min="1" max="9" value={newSearch.passengers} onChange={e => setNewSearch({ ...newSearch, passengers: parseInt(e.target.value) || 1 })} />
            </div>
          </div>

          <div className="flight-form-row">
            <div className="flight-field">
              <label>Earliest date</label>
              <input type="date" value={newSearch.dateRangeStart} onChange={e => setNewSearch({ ...newSearch, dateRangeStart: e.target.value })} required />
            </div>
            <div className="flight-field">
              <label>Latest date</label>
              <input type="date" value={newSearch.dateRangeEnd} onChange={e => setNewSearch({ ...newSearch, dateRangeEnd: e.target.value })} required />
            </div>
          </div>

          <div className="flight-form-row">
            <div className="flight-field">
              <label>Min trip days</label>
              <input type="number" min="1" max="30" value={newSearch.tripDurationMinDays} onChange={e => setNewSearch({ ...newSearch, tripDurationMinDays: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="flight-field">
              <label>Max trip days</label>
              <input type="number" min="1" max="30" value={newSearch.tripDurationMaxDays} onChange={e => setNewSearch({ ...newSearch, tripDurationMaxDays: parseInt(e.target.value) || 1 })} />
            </div>
          </div>

          <div className="flight-form-row">
            <div className="flight-field">
              <label>Weekday preference</label>
              <select value={newSearch.weekdayTimePreference} onChange={e => setNewSearch({ ...newSearch, weekdayTimePreference: parseInt(e.target.value) })}>
                {Object.entries(timePreferenceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flight-field">
              <label>Weekend preference</label>
              <select value={newSearch.weekendTimePreference} onChange={e => setNewSearch({ ...newSearch, weekendTimePreference: parseInt(e.target.value) })}>
                {Object.entries(timePreferenceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="flight-form-actions">
            <button type="submit" className="flight-btn primary">🔍 Start Searching</button>
            <button type="button" className="flight-btn secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flight-empty">Loading searches...</div>
      ) : searches.length === 0 ? (
        <div className="flight-empty">No searches yet. Create one to find cheap flights! ✈️</div>
      ) : (
        <div className="flight-search-cards">
          {searches.map(s => (
            <div key={s.id} className={`flight-search-card ${selectedSearch?.id === s.id ? 'active' : ''}`} onClick={() => openSearch(s.id)}>
              <div className="flight-search-card-header">
                <span className="flight-route">{s.origin} → {s.destination}</span>
                <span className={`flight-status ${statusKeys[s.status]}`}>{statusLabels[s.status]}</span>
              </div>
              <div className="flight-search-card-detail">
                📅 {formatDate(s.dateRangeStart)} — {formatDate(s.dateRangeEnd)}
              </div>
              <div className="flight-search-card-detail">
                🕐 {s.tripDurationMinDays}–{s.tripDurationMaxDays} days • {s.resultCount || 0} flights found
              </div>
              <div className="flight-search-card-actions">
                <button className="flight-btn icon-only" onClick={(e) => { e.stopPropagation(); deleteSearch(s.id); }}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // --- Render: Calendar view ---
  const renderCalendar = () => {
    if (!selectedSearch || calendarData.length === 0) {
      return <div className="flight-empty">No calendar data available</div>;
    }

    const prices = calendarData.map(d => d.lowestPrice).filter(p => p > 0);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    const getPriceColor = (price) => {
      if (prices.length <= 1) return 'medium';
      const ratio = (price - minPrice) / (maxPrice - minPrice);
      if (ratio <= 0.33) return 'cheap';
      if (ratio <= 0.66) return 'medium';
      return 'expensive';
    };

    // Group by month
    const byMonth = {};
    calendarData.forEach(d => {
      const date = new Date(d.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push(d);
    });

    return (
      <div className="flight-calendar">
        {Object.entries(byMonth).map(([month, days]) => {
          const monthDate = new Date(month + '-01');
          const monthName = monthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
          return (
            <div key={month} className="flight-calendar-month">
              <h4>{monthName}</h4>
              <div className="flight-calendar-grid">
                {days.sort((a, b) => new Date(a.date) - new Date(b.date)).map(d => {
                  const date = new Date(d.date);
                  const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  return (
                    <div key={d.date} className={`flight-calendar-cell ${getPriceColor(d.lowestPrice)} ${isWeekend ? 'weekend' : ''}`}
                         onClick={() => scrollToDate(d.date)}>
                      <span className="cal-day">{dayName}</span>
                      <span className="cal-date">{date.getDate()}</span>
                      <span className="cal-price">{d.lowestPrice > 0 ? `${Math.round(d.lowestPrice)}€` : '—'}</span>
                      <span className="cal-count">{d.flightCount} flights</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const scrollToDate = (date) => {
    setView('list');
    // Scroll to the date section in the results
    setTimeout(() => {
      const el = document.getElementById(`flight-date-${date}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // --- Render: Results list ---
  const renderResults = () => {
    if (!selectedSearch) return null;
    const results = unwrap(selectedSearch.results);
    if (results.length === 0) {
      if (selectedSearch.status <= 1) {
        return <div className="flight-empty">🔍 Searching for flights... Results will appear here.</div>;
      }
      if (selectedSearch.status === 3) {
        const msg = selectedSearch.errorMessage || 'Unknown error';
        const isBlocked = msg.toLowerCase().includes('blocked') || msg.toLowerCase().includes('rate limit');
        return <div className="flight-empty flight-error">
          {isBlocked ? '🚫' : '❌'} {isBlocked ? 'Rate Limited' : 'Search failed'}
          <p style={{fontSize: '0.9rem', marginTop: '0.5rem'}}>{msg.substring(0, 300)}</p>
          {isBlocked && <p style={{fontSize: '0.85rem', color: '#888', marginTop: '0.25rem'}}>
            SAS has temporarily blocked automated searches. Try again in 10-15 minutes.
          </p>}
        </div>;
      }
      return <div className="flight-empty">✈️ No matching flights found for this route/date combination.</div>;
    }

    // Group by date
    const byDate = {};
    results.forEach(r => {
      const dateKey = r.departureDate?.split('T')[0] || 'unknown';
      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push(r);
    });

    return (
      <div className="flight-results">
        {Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, flights]) => (
          <div key={date} id={`flight-date-${date}`} className="flight-date-group">
            <h4 className="flight-date-header">{formatDay(date)}</h4>
            {flights.sort((a, b) => (a.departureTime || '').localeCompare(b.departureTime || '')).map(f => (
              <div key={f.id} className={`flight-result-card ${f.isFavorite ? 'favorite' : ''}`}>
                <div className="flight-result-main">
                  <div className="flight-times">
                    <span className="flight-dep">{f.departureTime || '—'}</span>
                    <span className="flight-arrow">→</span>
                    <span className="flight-arr">{f.arrivalTime || '—'}</span>
                  </div>
                  <div className="flight-meta">
                    <span className="flight-duration">{f.duration || '—'}</span>
                    <span className={`flight-stops ${f.stops === 0 ? 'direct' : ''}`}>
                      {f.stops === 0 ? 'Direct' : `${f.stops} stop${f.stops > 1 ? 's' : ''}`}
                    </span>
                    {f.airline && <span className="flight-airline">{f.airline}</span>}
                  </div>
                </div>
                <div className="flight-prices">
                  {f.priceGo && (
                    <div className="flight-price economy">
                      <span className="price-label">Economy</span>
                      <span className="price-value">{Math.round(f.priceGo)} {f.currency}</span>
                    </div>
                  )}
                  {f.pricePlus && (
                    <div className="flight-price business">
                      <span className="price-label">Business</span>
                      <span className="price-value">{Math.round(f.pricePlus)} {f.currency}</span>
                    </div>
                  )}
                </div>
                <div className="flight-result-actions">
                  <button className={`flight-btn icon-only ${f.isFavorite ? 'favorited' : ''}`}
                          onClick={() => toggleFavorite(f.id)}>
                    {f.isFavorite ? '⭐' : '☆'}
                  </button>
                  {f.bookingUrl && (
                    <a href={f.bookingUrl} target="_blank" rel="noopener noreferrer" className="flight-btn primary small">
                      Book →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // --- Render: Search detail ---
  const renderSearchDetail = () => {
    if (!selectedSearch) return null;
    return (
      <div className="flight-detail">
        <div className="flight-detail-header">
          <div>
            <h3>{selectedSearch.origin} → {selectedSearch.destination}</h3>
            <span className="flight-detail-dates">
              {formatDate(selectedSearch.dateRangeStart)} — {formatDate(selectedSearch.dateRangeEnd)}
              {' '}• {selectedSearch.tripDurationMinDays}–{selectedSearch.tripDurationMaxDays} days
            </span>
          </div>
          <div className="flight-view-toggle">
            <button className={`flight-btn small ${view === 'list' ? 'primary' : 'secondary'}`} onClick={() => setView('list')}>📋 List</button>
            <button className={`flight-btn small ${view === 'calendar' ? 'primary' : 'secondary'}`} onClick={() => setView('calendar')}>📅 Calendar</button>
          </div>
        </div>
        {selectedSearch.status <= 1 && (
          <div className="flight-searching-banner">
            <div className="flight-spinner" />
            Searching for flights... This may take a few minutes.
          </div>
        )}
        {view === 'calendar' ? renderCalendar() : renderResults()}
      </div>
    );
  };

  return (
    <div className="flight-finder">
      <AppHeader
        title="Flight Finder"
        icon="✈️"
        description="Search SAS.no for the best flight deals across flexible dates"
      />
      <div className="flight-content">
        {renderSearchList()}
        {renderSearchDetail()}
      </div>
    </div>
  );
}
